import { useEffect, useId, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { MODAL_INPUT_CLASS, ModalFooter, ModalHeader, ModalTextarea } from '@/components/admin/editorial-modal';
import { Button } from '@/components/ui/button';
import { ChipRadioGroup } from '@/components/ui/chip-radio-group';
import { Check, HelpCircle, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { labelFor } from '@/i18n/labels';
import { QUESTION_CATEGORY_LABEL_KEY, QUESTION_PRIORITY_LABEL_KEY } from '@/lib/medical-display';
import { useCompanionName } from '@/hooks/useCompanionName';
import { FieldLabel, MonoLabel } from '@/components/editorial';
import { safeFormatIntl } from '@/lib/safe-date';
import type { DoctorQuestion, MedicalAppointment } from '@/types/api';

const PRIORITIES: DoctorQuestion['priority'][] = ['this_visit', 'can_wait', 'already_asked'];
const CATEGORIES: DoctorQuestion['category'][] = [
  'side_effects', 'next_steps', 'medications', 'test_results', 'treatment', 'other',
];

interface AddQuestionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointments: MedicalAppointment[];
  /**
   * Optional pre-drafted question text. Used by the Stevens "symptom-to-
   * question elevation" callout — Stevens drafts the question, parent
   * reviews and edits before saving.
   */
  prefillQuestion?: string | null;
  /** When set, the modal hydrates from this record and submits an update
   *  instead of a create — the question TEXT itself is editable (#847). */
  editing?: DoctorQuestion | null;
  onSubmit: (data: Partial<DoctorQuestion>) => void;
  isSubmitting?: boolean;
}

const EMPTY: Partial<DoctorQuestion> = {
  question: '',
  linked_appointment_id: '',
  // Default to "can wait" so the auto-bump-on-link actually does something.
  // If the parent links a question to an appointment in the next 7 days
  // we'll bump to "this_visit" automatically (unless they've already
  // touched the priority chip).
  priority: 'can_wait',
  category: 'side_effects',
};

export function AddQuestionModal({
  open,
  onOpenChange,
  appointments,
  prefillQuestion,
  editing,
  onSubmit,
  isSubmitting,
}: AddQuestionModalProps) {
  const { t } = useTranslation(['health', 'common']);
  const companionName = useCompanionName();
  const [form, setForm] = useState<Partial<DoctorQuestion>>(EMPTY);
  // Tracks whether the parent has manually picked a priority — once they
  // have, the auto-bump on linked-appointment-change stops firing so we
  // never overwrite an explicit choice.
  const [priorityTouched, setPriorityTouched] = useState(false);
  const isDrafted = Boolean(prefillQuestion);
  const isEdit = Boolean(editing);
  const priorityLabelId = useId();
  const categoryLabelId = useId();

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        question: editing.question,
        linked_appointment_id: editing.linked_appointment_id ?? '',
        priority: editing.priority,
        category: editing.category,
      });
      // An existing record's priority is an explicit prior choice — never
      // auto-bump it when the linked appointment changes.
      setPriorityTouched(true);
    } else {
      setForm({ ...EMPTY, question: prefillQuestion || '' });
      setPriorityTouched(false);
    }
  }, [open, prefillQuestion, editing]);

  const valid = (form.question?.trim() ?? '').length > 0;

  // Suggest the next upcoming appointment as default link target. When
  // editing, keep the currently-linked appointment selectable even if it has
  // since concluded — otherwise the select would silently drop the link.
  const upcoming = appointments
    .filter((a) => a.status === 'scheduled' || (isEdit && a._id === editing?.linked_appointment_id))
    .sort((a, b) => new Date(a.date_time).getTime() - new Date(b.date_time).getTime());

  // When the parent links a question to an appointment that's < 7 days out,
  // bump the priority to "this_visit" automatically. Only fires once per
  // modal session and only if the parent hasn't touched the priority chip.
  const handleLinkedApptChange = (apptId: string) => {
    setForm((f) => ({ ...f, linked_appointment_id: apptId }));
    if (priorityTouched || !apptId) return;
    const appt = appointments.find((a) => a._id === apptId);
    if (!appt) return;
    const ms = new Date(appt.date_time).getTime() - Date.now();
    if (ms > 0 && ms <= 7 * 24 * 60 * 60 * 1000) {
      setForm((f) => ({ ...f, priority: 'this_visit' }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl flex flex-col p-0 gap-0 max-h-[90vh]">
        <ModalHeader
          icon={HelpCircle}
          eyebrow={isEdit ? t('health:modal.question.editEyebrow') : isDrafted ? t('health:modal.question.draftedEyebrow', { companion: companionName }) : t('health:modal.question.newEyebrow')}
          title={isEdit ? t('health:modal.question.editTitle') : isDrafted ? t('health:modal.question.draftedTitle') : t('health:modal.question.newTitle')}
        />

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5 min-h-0">
          <Field label={t('health:modal.question.field.question')}>
            <ModalTextarea
              value={form.question ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
              // Gender-neutral (R3-B22; research §2.2 rule 4): the old example said "she".
              placeholder={t('health:modal.question.placeholder')}
              rows={3}
              className="text-lede"
              autoFocus
            />
          </Field>

          <FieldGroup label={t('health:modal.question.field.priority')} labelId={priorityLabelId}>
            <ChipRadioGroup
              labelledBy={priorityLabelId}
              value={form.priority ?? 'can_wait'}
              onValueChange={(p) => { setForm((f) => ({ ...f, priority: p })); setPriorityTouched(true); }}
              chipClassName="justify-center text-center"
              options={PRIORITIES.map((p) => ({ value: p, label: labelFor(QUESTION_PRIORITY_LABEL_KEY, p, t) }))}
            />
          </FieldGroup>

          <FieldGroup label={t('health:modal.question.field.category')} labelId={categoryLabelId}>
            <ChipRadioGroup
              labelledBy={categoryLabelId}
              value={form.category ?? 'side_effects'}
              onValueChange={(c) => setForm((f) => ({ ...f, category: c }))}
              chipClassName="justify-center text-center"
              options={CATEGORIES.map((c) => ({ value: c, label: labelFor(QUESTION_CATEGORY_LABEL_KEY, c, t) }))}
            />
          </FieldGroup>

          <Field label={t('health:modal.question.field.appointment')}>
            <select
              value={form.linked_appointment_id ?? ''}
              onChange={(e) => handleLinkedApptChange(e.target.value)}
              className={MODAL_INPUT_CLASS}
            >
              <option value="">{t('health:modal.question.noAppointment')}</option>
              {upcoming.map((a) => (
                <option key={a._id} value={a._id}>
                  {t('health:modal.question.appointmentOption', { title: a.title, date: safeFormatIntl(a.date_time, 'monthDayYear') })}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <ModalFooter
          wrap
          lead={
            <MonoLabel className="text-[11px] font-normal">
              {form.priority === 'this_visit' ? t('health:modal.question.footerNextVisit') : t('health:modal.question.footerLater')}
            </MonoLabel>
          }
        >
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            size="sm"
            variant="brand"
            disabled={!valid || isSubmitting}
            onClick={() => onSubmit(form)}
          >
            {isEdit ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            {isSubmitting ? t('common:states.saving') : isEdit ? t('common:actions.saveChanges') : t('health:modal.question.submit')}
          </Button>
        </ModalFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <FieldLabel as="span" className="mb-1.5">{label}</FieldLabel>
      {children}
    </label>
  );
}

/** A heading over a chip group: a span the group points at with
 *  aria-labelledby, never a wrapping <label> (#2434). */
function FieldGroup({ label, labelId, children }: { label: string; labelId: string; children: React.ReactNode }) {
  return (
    <div>
      <FieldLabel as="span" id={labelId} className="mb-1.5">{label}</FieldLabel>
      {children}
    </div>
  );
}
